import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowLeft, Gift, Search, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { AdminLayout } from '@/app/shell/admin-layout';
import { useAdminPlans } from '@/shared/api/admin-plans';
import { type CustomerType, useGrantSubscription } from '@/shared/api/admin-subscriptions';
import { api } from '@/shared/api/client';
import { cn } from '@/shared/lib/cn';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Manual subscription grant form.  Phase 1 monetization path:
 *   Founder fills this in for each beta tester / partnership / gift.
 *   Result: customer gets the chosen plan, immediately effective.
 *
 * Customer-id input is a plain UUID field — admin pastes from Users /
 * Teams pages.  Picker UI is a Phase 1.5 improvement.
 *
 * Prefill via URL search: ?customerType=user&customerId=...  Used by the
 * "Grant subscription" link from User / Team detail pages.
 */
export function GrantSubscriptionPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as {
    customerType?: CustomerType;
    customerId?: string;
  };

  const [customerType, setCustomerType] = useState<CustomerType>(search.customerType ?? 'team');
  const [customerId, setCustomerId] = useState(search.customerId ?? '');
  const [planCode, setPlanCode] = useState<string>('');
  const [lifetime, setLifetime] = useState(false);
  const [durationDays, setDurationDays] = useState<string>('90');
  const [reason, setReason] = useState('');

  const { data: plans } = useAdminPlans();
  const grantMut = useGrantSubscription();

  // Plans for the selected customer type.  Personal-pro for user, team for team.
  const availablePlans = (plans ?? []).filter((p) => {
    if (p.code === 'free') return false;
    if (customerType === 'user') return p.code === 'personal_pro';
    if (customerType === 'team') return p.code !== 'personal_pro';
    return true;
  });

  const isUuid = UUID_RE.test(customerId.trim());
  const reasonTrimmed = reason.trim();
  const durationNum = Number(durationDays);
  const validDuration = lifetime || (Number.isFinite(durationNum) && durationNum > 0 && durationNum <= 3650);
  const canSubmit =
    isUuid &&
    planCode &&
    reasonTrimmed.length >= 10 &&
    validDuration &&
    !grantMut.isPending;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      const sub = await grantMut.mutateAsync({
        customerType,
        customerId: customerId.trim(),
        planCode,
        durationDays: lifetime ? null : durationNum,
        reason: reasonTrimmed,
      });
      toast.success(`Granted ${sub.planCode} (${sub.status}) to ${customerType}/${customerId.slice(0, 8)}…`);
      await navigate({ to: '/subscriptions' });
    } catch {
      /* MutationCache toasts the error */
    }
  }

  return (
    <AdminLayout>
      <Link
        to="/subscriptions"
        className="mb-4 inline-flex items-center gap-1 text-sm text-neutral-400 hover:text-neutral-200"
      >
        <ArrowLeft size={14} /> All subscriptions
      </Link>

      <header className="mb-6">
        <h1 className="text-xl font-semibold">Grant subscription</h1>
        <p className="text-sm text-neutral-400">
          Manually activate Pro / Team for a user or team.  No payment processed — this is the
          Phase 1 path for beta testers, gifts, and partnership deals.  Audit-logged.
        </p>
      </header>

      <form
        onSubmit={onSubmit}
        className="max-w-xl space-y-5 rounded-xl bg-neutral-900 p-6 ring-1 ring-neutral-800"
      >
        {/* Customer type */}
        <Field label="Customer type">
          <div className="flex gap-2">
            <RadioPill
              label="Team"
              active={customerType === 'team'}
              onClick={() => {
                setCustomerType('team');
                setPlanCode('');
                setCustomerId('');
              }}
            />
            <RadioPill
              label="User (personal)"
              active={customerType === 'user'}
              onClick={() => {
                setCustomerType('user');
                setPlanCode('');
                setCustomerId('');
              }}
            />
          </div>
        </Field>

        {/* Customer — search by name/email/slug, or paste a UUID */}
        <Field
          label={customerType === 'team' ? 'Team' : 'User'}
          hint="Search by name / email / slug — or paste a UUID."
        >
          <CustomerPicker customerType={customerType} value={customerId} onChange={setCustomerId} />
        </Field>

        {/* Plan */}
        <Field label="Plan">
          <select
            value={planCode}
            onChange={(e) => setPlanCode(e.target.value)}
            className="w-full rounded-md bg-neutral-800 px-3 py-2 text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
          >
            <option value="">Choose a plan…</option>
            {availablePlans.map((p) => (
              <option key={p.code} value={p.code}>
                {p.name} — ${(p.monthlyPriceCents / 100).toFixed(2)}/mo
              </option>
            ))}
          </select>
        </Field>

        {/* Duration */}
        <Field label="Duration" hint="Lifetime = no expiry.  Otherwise specify days (1..3650).">
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-neutral-300">
              <input
                type="checkbox"
                checked={lifetime}
                onChange={(e) => setLifetime(e.target.checked)}
                className="h-4 w-4 rounded border-neutral-600 bg-neutral-800"
              />
              Lifetime
            </label>
            <input
              type="number"
              min={1}
              max={3650}
              value={durationDays}
              disabled={lifetime}
              onChange={(e) => setDurationDays(e.target.value)}
              className="w-32 rounded-md bg-neutral-800 px-3 py-2 text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500 disabled:opacity-40"
            />
            <span className="text-sm text-neutral-500">days</span>
          </div>
        </Field>

        {/* Reason */}
        <Field label="Reason" hint="≥10 chars.  Recorded in audit log + billing event.">
          <textarea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Beta tester (signed up via direct message), 90-day gift."
            className="w-full resize-none rounded-md bg-neutral-800 px-3 py-2 text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
          />
        </Field>

        <div className="flex items-center justify-end gap-2 border-t border-neutral-800 pt-4">
          <Link
            to="/subscriptions"
            className="rounded-md px-3 py-1.5 text-sm text-neutral-400 hover:bg-neutral-800 hover:text-neutral-200"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={!canSubmit}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-medium text-white',
              canSubmit ? 'bg-orange-600 hover:bg-orange-700' : 'cursor-not-allowed bg-neutral-800 opacity-50',
            )}
          >
            <Gift size={14} />
            {grantMut.isPending ? 'Granting…' : 'Grant subscription'}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between">
        <label className="text-xs uppercase tracking-wider text-neutral-400">{label}</label>
        {hint ? <span className="text-xs text-neutral-600">{hint}</span> : null}
      </div>
      {children}
    </div>
  );
}

interface PickerResult {
  id: string;
  label: string;
  sub: string;
}

/**
 * Customer search picker — replaces pasting a raw UUID. Searches users (by
 * name/email) or teams (by name/slug) as you type; selecting fills the
 * customerId. Pasting a UUID still works (offered as a direct option).
 */
function CustomerPicker(props: {
  customerType: CustomerType;
  value: string;
  onChange: (id: string) => void;
}) {
  const { customerType, value, onChange } = props;
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const trimmed = query.trim();
  const isUuidQuery = UUID_RE.test(trimmed);
  const enabled = trimmed.length >= 2 && !isUuidQuery;

  const { data: results, isFetching } = useQuery({
    queryKey: ['admin', 'customer-search', customerType, trimmed],
    queryFn: async (): Promise<PickerResult[]> => {
      const qs = `search=${encodeURIComponent(trimmed)}&limit=8`;
      if (customerType === 'user') {
        const r = await api<{ items: Array<{ id: string; name: string; username: string; email: string }> }>(
          `/api/admin/users?${qs}`,
        );
        return r.items.map((u) => ({ id: u.id, label: u.name || u.username, sub: u.email }));
      }
      const r = await api<{ items: Array<{ id: string; name: string; slug: string }> }>(
        `/api/admin/teams?${qs}`,
      );
      return r.items.map((t) => ({ id: t.id, label: t.name, sub: t.slug }));
    },
    enabled,
    placeholderData: (prev) => prev,
  });

  function select(id: string) {
    onChange(id);
    setQuery('');
    setOpen(false);
  }

  if (value) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md bg-neutral-800 px-3 py-2 ring-1 ring-neutral-700">
        <code className="truncate text-sm text-neutral-200">{value}</code>
        <button
          type="button"
          onClick={() => onChange('')}
          className="shrink-0 text-neutral-500 hover:text-neutral-200"
          title="Change"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="flex items-center gap-2 rounded-md bg-neutral-800 px-3 py-2 ring-1 ring-neutral-700 focus-within:ring-2 focus-within:ring-orange-500">
        <Search size={14} className="shrink-0 text-neutral-500" />
        <input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={customerType === 'team' ? 'Search teams…' : 'Search users…'}
          className="w-full bg-transparent text-sm text-neutral-100 outline-none"
        />
      </div>
      {open && (isUuidQuery || enabled) ? (
        <ul className="absolute z-10 mt-1 max-h-64 w-full overflow-auto rounded-md border border-neutral-700 bg-neutral-900 py-1 shadow-xl">
          {isUuidQuery ? (
            <li>
              <button
                type="button"
                onClick={() => select(trimmed)}
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-neutral-800"
              >
                <span className="text-neutral-200">Use this ID</span>
                <code className="text-xs text-neutral-500">{trimmed.slice(0, 8)}…</code>
              </button>
            </li>
          ) : isFetching && !results ? (
            <li className="px-3 py-2 text-sm text-neutral-500">Searching…</li>
          ) : results && results.length > 0 ? (
            results.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => select(r.id)}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-neutral-800"
                >
                  <span className="truncate text-sm text-neutral-200">{r.label}</span>
                  <span className="shrink-0 truncate text-xs text-neutral-500">{r.sub}</span>
                </button>
              </li>
            ))
          ) : (
            <li className="px-3 py-2 text-sm text-neutral-500">No matches.</li>
          )}
        </ul>
      ) : null}
    </div>
  );
}

function RadioPill(props: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      className={cn(
        'rounded-md px-3 py-1.5 text-sm font-medium ring-1 transition-colors',
        props.active
          ? 'bg-orange-500/20 text-orange-300 ring-orange-500/50'
          : 'bg-neutral-800 text-neutral-300 ring-neutral-700 hover:bg-neutral-700',
      )}
    >
      {props.label}
    </button>
  );
}
